import React, { useState } from 'react';
import { MessageSquare, X, Send, Loader2, Bug, Lightbulb, MessageCircle } from 'lucide-react';
import { db } from '../../firebase';
import { UserProfile } from '../../types';
import { useToast } from './Toast';

interface FeedbackWidgetProps {
  user: UserProfile;
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ user }) => {
  const { addToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<'bug' | 'feature' | 'general'>('general');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    try {
      await db.collection('feedback').add({
        userId: user.uid,
        userEmail: user.email,
        type,
        message,
        url: window.location.href,
        createdAt: new Date(),
        status: 'new'
      });
      addToast("Thanks for your feedback!", 'success');
      setMessage('');
      setIsOpen(false);
    } catch (error: any) {
      console.error("Feedback error", error);
      if (error.code === 'permission-denied') {
          addToast("Error: Database permission denied. Please contact support.", 'error');
      } else {
          addToast("Failed to send feedback.", 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {/* Feedback Form Popover */}
      {isOpen && (
        <div className="mb-4 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-fade-in-up origin-bottom-right">
          <div className="bg-slate-900 p-4 flex justify-between items-center text-white">
            <h3 className="font-bold flex items-center gap-2">
              <MessageSquare size={18} className="text-primary-400" /> 
              Send Feedback
            </h3>
            <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">I want to...</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setType('bug')}
                  className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium flex flex-col items-center gap-1 transition-all ${type === 'bug' ? 'bg-red-50 text-red-700 ring-2 ring-red-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                >
                  <Bug size={16} /> Report Bug
                </button>
                <button
                  type="button"
                  onClick={() => setType('feature')}
                  className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium flex flex-col items-center gap-1 transition-all ${type === 'feature' ? 'bg-indigo-50 text-indigo-700 ring-2 ring-indigo-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                >
                  <Lightbulb size={16} /> Request Feature
                </button>
                <button
                  type="button"
                  onClick={() => setType('general')}
                  className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium flex flex-col items-center gap-1 transition-all ${type === 'general' ? 'bg-primary-50 text-primary-700 ring-2 ring-primary-200' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                >
                  <MessageCircle size={16} /> Chat
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={type === 'bug' ? "What happened? Steps to reproduce..." : type === 'feature' ? "It would be great if..." : "Tell us what you think..."}
                className="w-full h-24 p-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !message.trim()}
              className="w-full bg-slate-900 text-white py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Send Feedback
            </button>
          </form>
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all transform hover:scale-105 ${isOpen ? 'bg-slate-200 text-slate-600 rotate-90' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
      >
        {isOpen ? <X size={24} /> : <MessageSquare size={24} />}
      </button>
    </div>
  );
};